import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { BookOpen, FileCheck, ArrowRight, HelpCircle, CheckCircle2, ChevronRight } from 'lucide-react';

export const AuthorPage: React.FC = () => {
  const { data: faqsRes } = useQuery({
    queryKey: ['public-faqs'],
    queryFn: () => api.get('/public/faqs').then((res) => res.data.data),
  });

  const faqs = faqsRes || [];

  return (
    <div className="space-y-16 py-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
          Author Resources &amp; Guidelines
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark">
          Publishing with For The Truth
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
          We treat authors as partners in ministry and scholarship. Discover our submission guidelines, author support systems, and editorial answers.
        </p>
      </section>

      {/* Submission Guidelines Box */}
      <section className="p-8 sm:p-10 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-50 border border-brand-200 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-brand-700" />
          </div>
          <div>
            <h2 className="font-serif text-2xl font-bold text-slate-900">Submission Guidelines</h2>
            <p className="text-xs text-slate-500 font-mono">For The Truth Publishing Acquisitions</p>
          </div>
        </div>

        <div className="space-y-4 text-xs sm:text-sm text-slate-600 leading-relaxed divide-y divide-slate-100">
          <div className="pt-3 first:pt-0 space-y-1">
            <h3 className="font-bold text-slate-900">1. Required Submission Materials</h3>
            <p>Every proposal should include a working title, author biography, table of contents, target audience statement, and sample chapter or complete manuscript in DOCX or PDF format.</p>
          </div>
          <div className="pt-3 space-y-1">
            <h3 className="font-bold text-slate-900">2. Theological &amp; Doctrinal Alignment</h3>
            <p>We welcome submissions grounded in classical biblical orthodoxy, historic reformed confessions, and rigorous pastoral theology.</p>
          </div>
          <div className="pt-3 space-y-1">
            <h3 className="font-bold text-slate-900">3. Assessment Timeline</h3>
            <p>Initial editorial assessment is completed within 3 to 5 business days, after which authors receive a formal acknowledgement and diagnostic review.</p>
          </div>
        </div>

        <div className="pt-2">
          <Link
            to="/submit-enquiry"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs transition-colors"
          >
            <span>Submit Proposal Online</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* Author FAQs */}
      {faqs.length > 0 && (
        <section className="space-y-6">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-brand-600" />
            <h2 className="font-serif text-2xl font-bold text-slate-900">Author Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq: any) => (
              <div key={faq.id} className="p-6 rounded-xl bg-white border border-editorial-border shadow-xs space-y-2">
                <h3 className="font-serif font-bold text-base text-slate-900">{faq.question}</h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
};
